import React from 'react';
import { Home, TrendingUp, Shield, BarChart3, ArrowRight, Building } from 'lucide-react';
import descriptionImage from '../../assets/medium-shot-people-sitting-together_23-2149271699.jpg_arrchf.jpg';

const RealEstatePage = () => {
  const services = [
    {
      icon: Building,
      title: 'Property Investment Analysis',
      description: 'We conduct thorough market research and property analysis to identify lucrative investment opportunities in residential and commercial real estate across diverse markets.'
    },
    {
      icon: TrendingUp,
      title: 'Portfolio Diversification',
      description: 'Our team helps you build a diversified real estate portfolio that balances risk and return, incorporating various property types and geographic locations for optimal performance.'
    },
    {
      icon: BarChart3,
      title: 'Market Research & Analytics',
      description: 'Stay ahead of market trends with our comprehensive research and analytics services, providing insights into property values, rental yields, and investment timing strategies.'
    },
    {
      icon: Shield,
      title: 'Risk Management',
      description: 'We implement robust risk management strategies to protect your real estate investments from market volatility and ensure sustainable long-term growth.'
    },
    {
      icon: Home,
      title: 'Property Management Support',
      description: 'Our comprehensive support services include property management guidance, tenant relations, and maintenance oversight to maximize your rental income potential.'
    },
    {
      icon: TrendingUp,
      title: 'Exit Strategy Planning',
      description: 'We develop strategic exit plans that optimize your returns, whether through property sales, refinancing, or portfolio restructuring to align with your financial goals.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/80 to-navy-800/60"></div>
        <div className="absolute inset-0">
          <img
            src="/images/sa-rapita-2483668_640.jpg"
            alt="Real Estate Investment"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold font-serif mb-4">Real Estate</h1>
          <p className="text-xl md:text-2xl text-gray-200">
            Home / <span className="text-gold-400">Real Estate</span>
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-6">
              Real Estate Investment at <span className="text-gold-600">COREVEST LIMITED</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Real estate has long been regarded as one of the most stable and lucrative investment options available. At COREVEST LIMITED, we recognize the potential that real estate offers to build wealth and provide steady income streams. Our comprehensive real estate investment services are designed to help you navigate the complex property market and make informed decisions that align with your financial objectives.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Whether you're interested in residential properties, commercial real estate, or mixed-use developments, our experienced team provides the expertise and support necessary to maximize your investment potential while minimizing risk.
              </p>
            </div>
            
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img
                  src={descriptionImage}
                  alt="Real Estate Strategy"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/50 to-transparent"></div>
              </div>
            </div>
          </div>

          {/* Services Section */}
          <div className="mb-20">
            <div className="text-center mb-16">
              <h3 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4">
                Comprehensive Real Estate Services
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <div key={index} className="group bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                    <div className="inline-flex p-4 bg-gradient-to-r from-gold-500 to-gold-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-navy-900 mb-4">
                      {service.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-12 text-center text-white">
            <h3 className="text-3xl md:text-4xl font-bold font-serif mb-6">
              Ready to Invest in Real Estate?
            </h3>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Build wealth through strategic real estate investments with expert guidance and comprehensive market analysis. 
              Start your property investment journey today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://online.amdconsults.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-gold-500 hover:bg-gold-600 text-white font-bold rounded-full transition-colors duration-300"
              >
                Start Investing
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <button className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-navy-900 font-bold rounded-full transition-all duration-300">
                Learn More
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RealEstatePage; 